import {
  Box,
  Divider,
  FormControl,
  InputAdornment,
  InputLabel,
  Menu,
  Select as MuiSelect,
  TextField,
  Tooltip,
} from '@mui/material'
import MenuItem from '@mui/material/MenuItem'
import { isString, merge, toString } from 'lodash'
import { ChevronsUpDown, HelpCircle, Inbox, Plus, Search, X } from 'lucide-react'
import { cloneElement, isValidElement, useCallback, useMemo, useRef, useState } from 'react'
import Button from './Button'
import { EmptyState } from './EmptyState'
import { Link } from './Link'

export const Select = ({
  value = '',
  onChange = () => {},
  options = [],
  type = 'select',
  icon,
  buttonProps = {},
  sx = {},
  loading = false,
  disabled = false,
  wrapperProps = {},
  disabledReason = '',
  label = '',
  labelProps = {},
  labelWrapperProps = {},
  tooltipMessage = '',
  subheader,
  showDivider = false,
  menuProps = {},
  allowFilter = false, // only works for 'type: menu'
  onAddNewClick,
  ...rest
}) => {
  const [anchorEl, setAnchorEl] = useState()
  const [search, setSearch] = useState('')
  const selectedOption = options?.find((option) => option.value === value)
  const inputRef = useRef()
  const close = () => {
    setSearch('') // Empty search value on close
    setAnchorEl(null)
  }
  /**
   * If menu is open and 'allowFilter' is true then auto focus on the field
   */
  const open = (e) => {
    setAnchorEl(e?.currentTarget)
    e.stopPropagation()
    if (!!allowFilter) {
      /**
       * Need to set a tiny timeout before focusing as the input is off screen
       */
      setTimeout(() => {
        inputRef?.current?.focus()
      }, 100)
    }
  }
  const getMenuItemDefaultStyles = useCallback(
    (option) => ({
      color: option?.isError ? 'error.main' : 'text.primary',
      gap: '10px',
      '&:hover': {
        color: option?.isError ? 'error.main' : 'text.primary',
      },
    }),
    []
  )
  const selectedLabel = selectedOption?.label || value

  /**
   * A function to handle options filtering if 'allowFilter: true' is passed
   */
  const filteredOptions = useMemo(() => {
    return options?.filter((opt) =>
      (isString(opt) ? opt : toString(opt?.value))?.toLowerCase().includes(search.toLowerCase())
    )
  }, [search, options])

  if (type === 'menu') {
    return (
      <Tooltip title={disabled && disabledReason}>
        <FormControl disabled={disabled} {...wrapperProps}>
          <Trigger
            icon={icon}
            open={open}
            loading={loading}
            disabled={disabled}
            buttonProps={buttonProps}
            selectedLabel={selectedLabel}
          />

          <Menu
            id="sort-menu"
            anchorEl={anchorEl}
            keepMounted
            open={!!anchorEl}
            disabled
            onClose={close}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            {...menuProps}
          >
            {subheader}
            {
              /**
               * Add a filter input if 'allowFilter' is passed
               */
              allowFilter && (
                <MenuItem
                  sx={{
                    position: 'sticky',
                    background: (theme) => `${theme.palette.secondary.main} !important`,
                    zIndex: 1,
                    top: 0,
                    padding: '0px !important',
                    borderBottom: (theme) => `1px solid ${theme.palette.border.main}`,
                    '&:hover': {
                      background: 'none ',
                    },
                  }}
                  onKeyDown={(e) => {
                    e.stopPropagation()
                  }}
                >
                  <TextField
                    inputRef={inputRef}
                    variant="standard"
                    placeholder="Search"
                    fullWidth
                    value={search}
                    sx={{
                      py: 0.5,
                      px: 1,
                    }}
                    onChange={(event) => {
                      event.stopPropagation()
                      setSearch(event.target.value)
                    }}
                    InputProps={{
                      disableUnderline: true,
                      autoComplete: 'off',
                      spellCheck: 'false',
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search size={14} />
                        </InputAdornment>
                      ),
                      endAdornment: !!onAddNewClick && (
                        <InputAdornment
                          position="end"
                          sx={{
                            gap: '4px',
                            color: '',
                          }}
                        >
                          {!!search ? (
                            <Button
                              isIconButton
                              variant="text"
                              size="tiny"
                              color="secondary"
                              onClick={() => setSearch('')}
                            >
                              <X size={16} />
                            </Button>
                          ) : (
                            // Add an empty div so that the dropdown maintains a constant width as the button appears/disappears
                            <Box sx={{ width: 27 }} />
                          )}
                          <Button
                            isIconButton
                            variant="contained"
                            color="secondary"
                            size="tiny"
                            onClick={() => {
                              close()
                              onAddNewClick?.()
                            }}
                            sx={{
                              '& svg': {
                                stroke: (theme) => theme.palette.primary.main,
                              },
                            }}
                          >
                            <Plus size={16} />
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />
                </MenuItem>
              )
            }
            {!filteredOptions?.length ? (
              <EmptyState
                label="No results found"
                icon={<Inbox size={25} />}
                textVariant="textSecondary"
                sx={{ gap: '10px', padding: '20px' }}
              />
            ) : (
              filteredOptions?.map((option, idx) => [
                isValidElement(option.component) ? (
                  cloneElement(option.component, {
                    key: `custom-select-custom-${idx}`,
                    onClick: () => {
                      close()
                      option.component?.props?.onClick?.()
                    },
                  })
                ) : (
                  <MenuItem
                    /**
                     * Don't pass onclick event if item is a link
                     */
                    onClick={
                      option.to
                        ? undefined
                        : (e) => {
                            e.stopPropagation()
                            if (option.onClick) {
                              option.onClick(option)
                            } else {
                              onChange(option)
                            }
                            close()
                          }
                    }
                    component={option.to ? Link : undefined}
                    to={option.to || undefined}
                    key={`custom-select-item-${option.value}-${idx}`}
                    sx={getMenuItemDefaultStyles(option)}
                    disabled={option.disabled}
                  >
                    {option.icon ? cloneElement(option.icon, { size: 16 }) : null}
                    {option.label}
                  </MenuItem>
                ),
                option.group && options[idx + 1] && option.group !== options[idx + 1]?.group ? (
                  <Divider
                    key={`custom-select-divider-${option.value}-${idx}`}
                    sx={{
                      marginTop: '0 !important',
                      marginBottom: '0 !important',
                    }}
                  />
                ) : null,
              ])
            )}
          </Menu>
        </FormControl>
      </Tooltip>
    )
  }

  return (
    <Tooltip title={disabledReason}>
      <FormControl disabled={disabled} {...wrapperProps}>
        {label && (
          <Box
            sx={merge(labelWrapperProps?.sx || {}, {
              display: 'flex',
              alginItems: 'center',
              gap: '5px',
            })}
            {...labelWrapperProps}
          >
            <InputLabel {...labelProps}>{label}</InputLabel>
            <Tooltip title={tooltipMessage} placement="top">
              <Box
                sx={{
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <HelpCircle size={12} />
              </Box>
            </Tooltip>
          </Box>
        )}

        <MuiSelect
          value={value}
          sx={merge({}, sx)}
          label={selectedLabel}
          onChange={(e) => {
            onChange(options.find((option) => option.value === e.target.value))
          }}
          {...rest}
        >
          {options?.map((option, idx) =>
            isValidElement(option) ? (
              cloneElement(option, { key: `custom-select-${option.value}-${idx}` })
            ) : (
              <MenuItem
                key={`custom-select-${option.value}-${idx}`}
                value={option.value}
                sx={getMenuItemDefaultStyles(option)}
              >
                {option.icon}
                {option.label}
              </MenuItem>
            )
          )}
        </MuiSelect>
      </FormControl>
    </Tooltip>
  )
}

const Trigger = ({ icon, open, loading, disabled, buttonProps, selectedLabel }) => {
  return icon ? (
    <Button
      onClick={open}
      size="small"
      variant="text"
      color="secondary"
      isIconButton
      {...buttonProps}
    >
      {icon}
    </Button>
  ) : (
    <Button
      aria-controls="select-menu"
      aria-haspopup="true"
      onClick={open}
      size="small"
      variant="outlined"
      color="secondary"
      endIcon={<ChevronsUpDown size={13} />}
      loading={loading}
      disabled={disabled}
      sx={merge({}, buttonProps?.sx || {})}
      {...buttonProps}
    >
      {selectedLabel || buttonProps?.children}
    </Button>
  )
}
